import {assertAndContinue} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Cells, RowDataUiData} from 'notebook/tables/constants/RowConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import getPlainTextTable from 'notebook/tables/export/getPlainTextTable';
import {getIdsFromTypedValue, getChipIdFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {
  BulkChipItemPrefetchQuery,
  BulkChipItemPrefetchQueryVariables,
} from 'ui-library/chips/__generated/BulkChipItemPrefetchQuery';
import {
  getPrefetchQueryVariables,
  BULK_CHIP_ITEM_PREFETCH_QUERY,
  getFragmentByChipId,
} from 'ui-library/chips/useSmartChipQuery';
import APIUtil from 'util/APIUtil';
import * as ExportUtil from 'util/ExportUtil';

/**
 * Create a plain text CSV representation of a table and it's computed values.
 *
 * @param table - the table to create a CSV of
 * @param computedTable - the evaluated table
 * @param headerRow - labels for each of the columns in the CSV
 * @param rowLabels - labels for each of the rows, e.g. the sample ids
 * @param rowLabelHeader - a header for the row label, if the rowLabels are provided
 * @param includeCellValueModifiers - include modifier text in the output, defaults to true if not provided
 */
export default async function makeTableCSV({
  table,
  computedTable,
  headerRow,
  rowLabels,
  rowLabelHeader,
  includeCellValueModifiers,
  rowUiData,
}: {
  table: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
  headerRow?: Array<string> | null;
  rowLabels?: Array<string> | null;
  rowLabelHeader?: string | null;
  includeCellValueModifiers?: boolean | null;
  rowUiData?: Array<RowDataUiData>;
}): Promise<string> {
  // Bulk query all link entities that are chips on UI.
  // Query result would be cached and used for verifying if an entity is accessible to the user.
  const ids = new Set<string>();
  computedTable.forEach((row) => {
    row.forEach((cell) => {
      // Checks if the cell contains a link TypedValue; if it does, collects all IDs from the links.
      const idsFromTypedValue = getIdsFromTypedValue(cell.value);
      if (idsFromTypedValue) {
        idsFromTypedValue.forEach((id) => ids.add(id));
      } else {
        // Checks if the cell contains a TypedValue that is rendered as a chip in the UI.
        const idFromTypedValue = getChipIdFromTypedValue(cell.value);
        if (idFromTypedValue) {
          ids.add(idFromTypedValue);
        }
      }
    });
  });
  // Filter ids to only API-identified values
  const apiIds = Array.from(ids).filter((id) => APIUtil.apiIdToMaybeType(id) != null);

  const variables = getPrefetchQueryVariables(apiIds);
  await doQuerySafe<BulkChipItemPrefetchQuery, BulkChipItemPrefetchQueryVariables>(
    {query: BULK_CHIP_ITEM_PREFETCH_QUERY},
    {variables, fetchPolicy: 'network-only'}
  );
  const inaccessibleIds = new Set<string>();
  apiIds.forEach((id) => {
    const fragment = readFragmentFromCache(getFragmentByChipId(id), id);
    assertAndContinue(fragment != null, 'Expected chip value to be in cache immediately after loading');
    if (fragment != null && isInaccessible(fragment)) {
      inaccessibleIds.add(id);
    }
  });

  const exportedRows = getPlainTextTable({
    table,
    computedTable,
    includeCellValueModifiers: includeCellValueModifiers == null ? true : includeCellValueModifiers,
    headerRow,
    rowLabels,
    rowLabelHeader,
    rowUiData,
    inaccessibleIds,
  });
  return ExportUtil.formatRows(exportedRows, ',');
}
